import { useState } from "react";

import { useTranslation } from "react-i18next";

import useAxiosPrivate from "@/api/hooks/useAxiosPrivate";
import { ALERT_STATUS } from "@/context/alert/types";
import { useVehiclesTableStore } from "@/stores/useTableStore";

import {
  GeneralDialogProps,
  INPUT_LENGTH,
  ReassignVehicleData,
  ROUTES,
  useMediaQueries,
  VEHICLES_ACTIONS
} from "../../../../../../shared";
import DialogActions from "../../../../../../shared/components/buttons/DialogActions";
import useResponse from "../../../../../../shared/hooks/useResponse";
import useVehiclesFormInputs from "../../../../../../shared/hooks/useVehiclesFormInputs";
import useTableItemDetails from "../../../../../DetailsSection/hooks/useTableItemDetails";
import Dialog from "../../../../../Dialog/Dialog";
import { DialogWidth } from "../../../../../Dialog/types/enums";
import useTutorialMode from "../../../../../TutorialMode/hooks/useTutorialMode";
import { STEP_GROUP } from "../../../../../TutorialMode/types";
import AccessibleImeiDialog from "../AccessibleImeiDialog/AccessibleImeiDialog";
import ConfirmEmptyImeiDialog from "../ConfirmEmptyImeiDialog/ConfirmEmptyImeiDialog";
import InaccessibleImeiDialog from "../InaccessibleImeiDialog/InaccessibleImeiDialog";
import AddVehicleDialogContent from "./AddVehicleDialogContent";

const AddVehiclesDialog = ({ isOpen, onClose }: GeneralDialogProps) => {
  const { t } = useTranslation();
  const { toMd } = useMediaQueries();

  const { postData } = useAxiosPrivate();

  const { getTableItemDetails } = useTableItemDetails();
  const { handleResponse, handleVehicleActionError } = useResponse();
  const {
    stepGroup,
    callTutorialWithTimeout,
    checkIfCorrectTutorialStep,
    setTutorialDetails
  } = useTutorialMode();

  const params = useVehiclesTableStore(state => state.params);

  const { contains: searchParams } = params;

  const {
    descriptionInputError,
    descriptionValue,
    imeiError,
    imeiIsValid,
    imeiValue,
    isVehicleDeactivated,
    nameInputError,
    vehicleName,
    shouldVehicleNameValidate,
    handleChangeDescription,
    handleChangeImeiValue,
    handleChangeNameValue,
    handleToggleDeactivateVehicle,
    resetAllValues,
    setImeiError,
    setNameInputError,
    validateDescriptionLength,
    validateImei,
    validateNameLength,
    handleCustomError
  } = useVehiclesFormInputs();

  const [isLoading, setIsLoading] = useState(false);
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);

  const [isReassignOpen, setIsReassignOpen] = useState(false);
  const [reassignData, setReassignData] = useState<ReassignVehicleData | null>(
    null
  );

  const [inaccessibleImeiOpen, setInaccessibleImeiOpen] = useState(false);

  const descriptionIsValid =
    (descriptionValue && descriptionValue.length <= INPUT_LENGTH.Description) ||
    !descriptionValue;

  const isPayloadReadyToConfirm =
    shouldVehicleNameValidate && descriptionIsValid && imeiIsValid;

  const handleCloseAddVehicleDialog = (checkForTutorial?: boolean) => {
    onClose(VEHICLES_ACTIONS.AddVehicle);

    if (
      checkIfCorrectTutorialStep(STEP_GROUP.TutorialAddVehicle, 0) &&
      checkForTutorial
    ) {
      callTutorialWithTimeout(true, 0, stepGroup, true, 200);
    }
  };

  const isEmptyImei = imeiValue === "";

  const payload = {
    imei: isEmptyImei ? null : imeiValue,
    name: vehicleName,
    comment: descriptionValue,
    disable: isVehicleDeactivated,
    forceReassignImei: false
  };

  const handleSubmit = async (forceReassignImei?: boolean) => {
    if (imeiIsValid) {
      try {
        setIsLoading(true);

        if (forceReassignImei) {
          payload.forceReassignImei = true;
        }

        const response = await postData(ROUTES.Vehicles, payload);
        const idFromResponseLocation = response.headers.location;

        await getTableItemDetails({
          itemId: idFromResponseLocation,
          successMessage: isEmptyImei
            ? undefined
            : t("Alert##add vehicle success"),
          leaveTableDetailsNotUpdated: true,
          valueInSearchField: searchParams ?? undefined
        });

        if (isEmptyImei) {
          handleResponse(
            ALERT_STATUS.Info,
            t("Alert##add vehicle empty imei info##title"),
            t("Alert##add vehicle empty imei info##description")
          );
        }

        handleCloseAddVehicleDialog(false);

        if (checkIfCorrectTutorialStep(STEP_GROUP.TutorialAddVehicle, 0)) {
          const newStepIndex = toMd ? 2 : 1;

          setTutorialDetails({
            isTutorialOpen: false,
            stepIndex: newStepIndex,
            stepGroup: STEP_GROUP.TutorialApplySchedule,
            isTutorialActive: true
          });

          callTutorialWithTimeout(
            true,
            newStepIndex,
            STEP_GROUP.TutorialApplySchedule,
            true,
            500
          );
        }
      } catch (error) {
        handleVehicleActionError(
          error,
          setIsLoading,
          setImeiError,
          setNameInputError,
          t,
          () =>
            handleCustomError(
              error,
              setReassignData,
              setIsReassignOpen,
              setInaccessibleImeiOpen
            )
        );
      }
    }

    setIsLoading(false);
  };

  const handleMainSubmit = () => {
    if (isEmptyImei) {
      return setIsConfirmOpen(true);
    }

    handleSubmit();
  };

  const handleConfirmClose = (withSubmit?: boolean) => {
    setIsConfirmOpen(false);
    withSubmit && handleSubmit();
  };

  const handleReassignClose = (withSubmit?: boolean) => {
    setIsReassignOpen(false);
    setReassignData(null);
    withSubmit && handleSubmit(true);
  };

  return (
    <>
      <Dialog
        title={t("Dialog##add vehicle")}
        description={t("Dialog##add vehicle description")}
        extendToMaxWidth={toMd}
        isTitleSeparator
        isActionsSeparator
        open={isOpen}
        close={handleCloseAddVehicleDialog}
        submit={handleMainSubmit}
        width={toMd ? undefined : DialogWidth.MinWidth}
        slotProps={{
          transition: { onExited: () => resetAllValues() }
        }}
        actions={
          <DialogActions
            onClose={handleCloseAddVehicleDialog}
            isLoading={isLoading}
            isreadyToConfirm={isPayloadReadyToConfirm}
            confirmationButtonText={t("Button##add")}
            confirmationButtonTestId="add-vehicle-button"
            cancelButtonText={t("Button##close")}
            cancelButtonTestId="close-add-vehicle"
          />
        }
      >
        <AddVehicleDialogContent
          payload={payload}
          onNameChange={handleChangeNameValue}
          onImeiValueChange={handleChangeImeiValue}
          onDescriptionChange={handleChangeDescription}
          toggleVehicleDeactivate={handleToggleDeactivateVehicle}
          imeiErrorText={imeiError}
          nameErrorText={nameInputError}
          validateImei={validateImei}
          validateName={validateNameLength}
          validateDescription={validateDescriptionLength}
          descriptionErrorText={descriptionIsValid ? "" : descriptionInputError}
          isLoading={isLoading}
        />
      </Dialog>

      <ConfirmEmptyImeiDialog
        isOpen={isConfirmOpen}
        onClose={() => handleConfirmClose(false)}
        onSubmit={() => handleConfirmClose(true)}
      />

      <AccessibleImeiDialog
        dialogData={reassignData}
        isOpen={isReassignOpen}
        onClose={() => handleReassignClose(false)}
        onSubmit={() => handleReassignClose(true)}
      />

      <InaccessibleImeiDialog
        isOpen={inaccessibleImeiOpen}
        onClose={() => setInaccessibleImeiOpen(false)}
      />
    </>
  );
};

export default AddVehiclesDialog;
